import { useEffect, useMemo } from 'react';
import { requestForQuoteProductName } from '~/utils/requestForQuoteProductName';
import {
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router';
import { useTranslation } from 'react-i18next';
import { ClipSpinner } from '~/components/ui';
import { useGetRequestForQuoteById } from '~/api';
import {
  RequestForQuoteStatus,
  type GetRequestForQuoteWithQuotesQuery,
} from '~/graphql/graphql';
import { RequestForQuoteSummary } from './sections/RequestForQuoteSummary';
import { comparedTallyOf } from './sections/quote-comparison/comparedTally';
import { QuoteComparisonSection } from './sections/quote-comparison/QuoteComparisonSection';
import { FulfillRequestForQuoteButton } from './sections/FulfillRequestForQuoteButton';
import { ArchiveRequestForQuoteButton } from './sections/ArchiveRequestForQuoteButton';
import { TitleWithArchivedBadge } from './sections/TitleWithArchivedBadge';
import {
  negotiationContextOf,
  negotiationContextsFor,
} from './sections/quoteNegotiation';
import { partitionArchivedQuotes } from '~/utils/requestForQuoteClosed';
import {
  buyListRequestForQuoteLink,
  isFirstHistoryEntry,
  isRequestForQuoteOwnViewRequested,
  REQUEST_FOR_QUOTE_REDIRECT_STATE,
} from '~/utils/requestForQuoteLinks';

const NO_QUOTES: NonNullable<
  GetRequestForQuoteWithQuotesQuery['quotesForRequestForQuote']
> = [];

export function RfqView() {
  const { requestForQuoteId } = useParams();

  if (!requestForQuoteId) {
    return <Navigate to="/dashboard" replace />;
  }

  return <RfqViewContent requestForQuoteId={requestForQuoteId} />;
}

function RfqViewContent({ requestForQuoteId }: { requestForQuoteId: string }) {
  const { t } = useTranslation('rfq-view');
  const location = useLocation();
  const { hash } = location;
  const [searchParams] = useSearchParams();
  const { data, isLoading, isError, error } =
    useGetRequestForQuoteById(requestForQuoteId);
  const requestForQuote = data?.requestForQuote;
  const allQuotes = data?.quotesForRequestForQuote ?? NO_QUOTES;
  const { activeQuotes: quotes, archivedQuotes } = useMemo(
    () => partitionArchivedQuotes(allQuotes),
    [allQuotes]
  );
  const negotiationByRequestForQuoteId = useMemo(
    () =>
      negotiationContextsFor(
        requestForQuote ? [{ ...requestForQuote, quotes: allQuotes }] : []
      ),
    [requestForQuote, allQuotes]
  );

  useEffect(() => {
    if (!hash || !requestForQuote) return;

    const targetElement = document.getElementById(hash.slice(1));
    targetElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [hash, requestForQuote]);

  if (isLoading) return <ClipSpinner />;
  if (isError) {
    return <div>{t('rfq_view.loading_error', { message: error.message })}</div>;
  }
  if (!requestForQuote) {
    return <Navigate to="/dashboard" replace />;
  }
  if (
    requestForQuote.buyList &&
    !isRequestForQuoteOwnViewRequested(searchParams)
  ) {
    return (
      <Navigate
        to={buyListRequestForQuoteLink(
          requestForQuote.buyList.id,
          requestForQuote.id
        )}
        state={
          isFirstHistoryEntry(location)
            ? REQUEST_FOR_QUOTE_REDIRECT_STATE
            : null
        }
        replace
      />
    );
  }

  const { groups } = negotiationContextOf(
    negotiationByRequestForQuoteId,
    requestForQuote.id
  );

  return (
    <div className="flex flex-col gap-6" data-testid="rfq-heading">
      <div className="flex items-center justify-between">
        <TitleWithArchivedBadge
          title={t('rfq_view.title', {
            productName:
              requestForQuoteProductName(requestForQuote) ??
              t('rfq_view.multiple_products'),
          })}
          isArchived={requestForQuote.status === RequestForQuoteStatus.Archived}
          archivedLabel={t('rfq_view.request_archived_label')}
          badgeTestId="request-archived-badge"
        />
        <div className="flex items-center gap-2">
          <ArchiveRequestForQuoteButton
            requestForQuote={requestForQuote}
            quotes={allQuotes}
          />
          <FulfillRequestForQuoteButton
            requestForQuote={requestForQuote}
            quotes={allQuotes}
          />
        </div>
      </div>
      <RequestForQuoteSummary
        requestForQuote={requestForQuote}
        groups={groups}
      />
      <QuoteComparisonSection
        comparisons={[
          {
            key: requestForQuote.id,
            tally: comparedTallyOf(requestForQuote),
            requestForQuote,
            quotes,
            archivedQuotes,
            groups,
          },
        ]}
        negotiationByRequestForQuoteId={negotiationByRequestForQuoteId}
        testId="rfq-quote-comparison"
      />
    </div>
  );
}
